import { useLocaleStore, useT } from '../../i18n';
import type { LEER, Schluessel, Wert } from '../../platform/einstellungen';
import { eintragsText } from './einstellungsFelder';
import { ordneVorschau, zeigeWert } from './qrWert';
import { Dialog } from '../../ui/Dialog';
import { Icon } from '../../ui/Icon';
import styles from './SetupSheet.module.css';

/**
 * Was ein gescannter Einstellungscode ändern würde — **vor** dem Ändern.
 *
 * ⚠️ **Keine stille Übernahme.** Der Code des Classic-Servers trägt Adresse, Anmeldenamen und
 * **Passwort** im Klartext (s. `domain/qrNutzlast.ts`) und setzt sich auf Wunsch des Nutzers
 * sogar über die Geräteverwaltung hinweg. Beides muss der Prüfer sehen, ehe es gilt — ein
 * iPad, dessen Serveradresse sich durch einen abfotografierten Zettel ändert, wäre sonst
 * nicht mehr nachvollziehbar.
 *
 * ⚠️ **Auch das Ignorierte wird genannt.** Die Mehrzahl der 41 Classic-Schalter hat bei uns
 * keine Entsprechung. Sie wortlos fallenzulassen hiesse, dem Prüfer eine vollständige
 * Übernahme vorzuspielen.
 */
export function QrUebernahmeDialog({
  uebernommen,
  ignoriert,
  gruppe = null,
  onUebernehmen,
  onClose,
}: {
  uebernommen: Partial<Record<Schluessel, Wert | typeof LEER | null>>;
  ignoriert: string[];
  /** Relays Gruppenname, nur zur Anzeige. */
  gruppe?: string | null;
  onUebernehmen: () => void;
  onClose: () => void;
}) {
  const t = useT();
  const locale = useLocaleStore((s) => s.locale);
  const eintraege = Object.entries(uebernommen) as [Schluessel, Wert | typeof LEER | null][];
  // Kurze Namen aus `t.qr.namen`, sonst die Beschriftung der Einstellungsseite — Relays Code
  // trägt alle Einstellungen, nicht nur die wenigen, die der Classic-Code kannte.
  const name = (s: Schluessel) => t.qr.namen[s] ?? eintragsText(s, locale);
  // Der Serverzugang zuoberst, die Warnung gleich darunter: zwischen 39 Zeilen ginge er unter.
  const { zugang, uebrige } = ordneVorschau(eintraege, name);
  const liste = (zeilen: typeof eintraege) => (
    <ul className={styles.qrListe}>
      {zeilen.map(([schluessel, wert]) => (
        <li key={schluessel}>
          <span className={styles.qrName}>{name(schluessel)}</span>
          <span className={styles.qrWert}>{zeigeWert(schluessel, wert, t.qr.leer)}</span>
        </li>
      ))}
    </ul>
  );
  const warnung = (
    <p className={styles.deleteWarn} data-schwer="true">
      <Icon name="warning" size={14} /> {t.qr.warnung}
    </p>
  );

  return (
    <Dialog kicker={t.qr.kicker} title={t.qr.title} onClose={onClose} width={520}>
      {gruppe && <p className={styles.deleteBody}>{t.qr.gruppe(gruppe)}</p>}
      {eintraege.length === 0 ? (
        <p className={styles.deleteWarn} data-schwer="true">{t.qr.nichts}</p>
      ) : (
        <>
          <p className={styles.deleteBody}>{t.qr.uebernimmt(eintraege.length)}</p>
          {zugang.length > 0 && (
            <>
              <p className={styles.qrAbschnitt}>{t.qr.zugang}</p>
              {liste(zugang)}
              {warnung}
            </>
          )}
          {uebrige.length > 0 && (
            <>
              {zugang.length > 0 && <p className={styles.qrAbschnitt}>{t.qr.uebrige}</p>}
              {liste(uebrige)}
            </>
          )}
          {zugang.length === 0 && warnung}
        </>
      )}

      {ignoriert.length > 0 && (
        <p className={styles.deleteBody}>{t.qr.ignoriert(ignoriert.length)}: {ignoriert.join(', ')}</p>
      )}

      <div className={styles.deleteButtons}>
        <button type="button" className={`tap ${styles.deleteCancel}`} onClick={onClose}>
          {t.qr.abbrechen}
        </button>
        <button
          type="button"
          className={`tap ${styles.deleteConfirm}`}
          disabled={eintraege.length === 0}
          onClick={() => {
            onUebernehmen();
            onClose();
          }}
        >
          {t.qr.uebernehmen}
        </button>
      </div>
    </Dialog>
  );
}
